import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { FolderModel } from '../../../../../../../typings/api/skymap/rest/v1/.common';
import { ActivateSkyMapConnectParameters } from '../../../../../../../typings/api/skymap/rest/v1/folder';
import { SkyMapAxiosServiceFactory } from '../../../../../services/axios/skymap-axios-service-factory';
import { RequestParamsToApiPaths } from '../../../../../utils/typescript-utils';
import { isDefined } from '../../../../../utils/variables';
import {
  errorUtils,
  getHttpStatusCode,
  getResponseErrorParameter,
  handleError,
  TranslatedError,
} from '../../../../api/error-handling';
import { Button } from '../../../button/button';
import { Dialog } from '../../../dialog/dialog';
import { InfoBox } from '../../../info-box/info-box';
import { FolderListViewContext } from '../../state/folder-list-view-state';

type OnCloseAction = 'canceled' | 'activated' | 'deactivated';

interface Props {
  folder: FolderModel;
  onClose: (action: OnCloseAction) => void;
}

enum Status {
  Idle,
  Saving,
  Error,
}

type ApiRequestPath = RequestParamsToApiPaths<ActivateSkyMapConnectParameters>;

const SkyMapConnectDialog = (props: Props) => {
  const { t } = useTranslation();
  const { currentSubFolders } = React.useContext(FolderListViewContext);

  const [status, setStatus] = React.useState(Status.Idle);
  const [errorMessages, setErrorMessages] = React.useState<TranslatedError<ApiRequestPath>[]>([]);
  const [activateButtonDisabled, setActivateButtonDisabled] = React.useState(false);
  const [cancelFlagOnClose, setCancelFlagOnClose] = React.useState<OnCloseAction>('canceled');

  const otherFolderIsConnected = currentSubFolders.find((x) => x.skyMapConnectFolder);

  const activateButtonClick = async () => {
    try {
      setStatus(Status.Saving);

      const folderService = SkyMapAxiosServiceFactory.instance.createFolderServiceV1();
      await folderService.activateSkyMapConnect({ path: { folderId: props.folder.id } });

      props.onClose('activated');
      setStatus(Status.Idle);
    } catch (err) {
      setStatus(Status.Error);

      handleError(err, ['path.folderId'], setErrorMessages);

      errorUtils.isResponseError(
        err,
        'conflict',
        'skymap_connect:folder_already_activated',
        (errorItem) => {
          const folderId = getResponseErrorParameter(errorItem, 'folderId');

          // Close dialog if folder was connected to SkyMap Connect by someone else.
          // Otherwise, if another folder was connected, display message.
          if (folderId?.value === props.folder.id) {
            props.onClose('activated');
          } else {
            setActivateButtonDisabled(true);
            setCancelFlagOnClose('activated');
          }
        },
      );
    }
  };

  const deactivateButtonClick = async () => {
    try {
      setStatus(Status.Saving);

      const folderService = SkyMapAxiosServiceFactory.instance.createFolderServiceV1();
      await folderService.deactivateSkyMapConnect({ path: { folderId: props.folder.id } });

      props.onClose('deactivated');
    } catch (err) {
      setStatus(Status.Error);
      handleError(err, ['path.folderId'], setErrorMessages);

      // Close dialog if folder was removed from SkyMap Connect (removed by someone else).
      if (getHttpStatusCode(err) === 404) {
        props.onClose('deactivated');
      }
    }
  };

  const cancelButtonClick = () => {
    props.onClose(cancelFlagOnClose);
  };

  const isConnectButtonDisabled = (): boolean =>
    status === Status.Saving || isDefined(otherFolderIsConnected) || activateButtonDisabled;

  const getOtherFolderAlreadyConnectedText = (name: string) => {
    return (
      <Trans
        components={{ br: <br />, bold: <strong /> }}
        i18nKey="fileManager.skyMapConnectDialog.anotherFolderConnected"
        ns="components"
        values={{ folderName: name }}
      />
    );
  };

  const renderContent = () => {
    if (props.folder.skyMapConnectFolder) {
      return (
        <>
          <Trans
            components={{ bold: <strong /> }}
            i18nKey="fileManager.skyMapConnectDialog.currentFolderConnected"
            ns="components"
            values={{ folderName: props.folder.name }}
          />
          <InfoBox color="yellow" leftIcon={{ icon: 'exclamation-triangle' }} topMargin={true}>
            {t('fileManager.skyMapConnectDialog.disconnectWarning', { ns: 'components' })}
          </InfoBox>
        </>
      );
    }

    if (otherFolderIsConnected) {
      return (
        <>
          <InfoBox color="red" topMargin={false}>
            {getOtherFolderAlreadyConnectedText(otherFolderIsConnected.name)}
          </InfoBox>
        </>
      );
    }

    if (status === Status.Idle || status === Status.Saving) {
      return (
        <>
          <Trans
            components={{ bold: <strong /> }}
            i18nKey="fileManager.skyMapConnectDialog.currentFolderNotConnected"
            ns="components"
            values={{ folderName: props.folder.name }}
          />
          <InfoBox color="yellow" topMargin={true}>
            {t('fileManager.skyMapConnectDialog.connectWarning', { ns: 'components' })}
          </InfoBox>
        </>
      );
    }

    if (status === Status.Error) {
      return (
        <>
          <span>
            <Trans
              components={{ bold: <strong /> }}
              i18nKey="fileManager.skyMapConnectDialog.currentFolderNotConnected"
              ns="components"
              values={{ folderName: props.folder.name }}
            />
          </span>
          <InfoBox color="red" topMargin={true}>
            {/*
              todo: Create <ErrorMessageList> component to render errors as a list where
                    bullet is shown ony when more than one item.
            */}
            {errorMessages.map((x) => {
              if (x.originalError?.code === 'skymap_connect:folder_already_activated') {
                const folderName = getResponseErrorParameter(x.originalError, 'folderName');
                return getOtherFolderAlreadyConnectedText(folderName!.value as string);
              }

              return x.errorMessage;
            })}
          </InfoBox>
        </>
      );
    }
  };

  return (
    <Dialog
      closeIcon={status !== Status.Saving}
      closeOnDimmerClick={true}
      width={350}
      onClose={cancelButtonClick}
    >
      {{
        header: 'SkyMap Connect',
        content: <Content>{renderContent()}</Content>,
        footer: {
          right: (
            <>
              {props.folder.skyMapConnectFolder ? (
                <Button
                  color="primary"
                  loading={status === Status.Saving}
                  variant="contained"
                  onClick={deactivateButtonClick}
                >
                  {t('fileManager.skyMapConnectDialog.disconnect', { ns: 'components' })}
                </Button>
              ) : (
                <Button
                  color="primary"
                  disabled={isConnectButtonDisabled()}
                  loading={status === Status.Saving}
                  variant="contained"
                  onClick={activateButtonClick}
                >
                  {t('fileManager.skyMapConnectDialog.connect', { ns: 'components' })}
                </Button>
              )}
              <Button
                disabled={status === Status.Saving}
                variant="text"
                onClick={cancelButtonClick}
              >
                {t('close', { ns: 'common' })}
              </Button>
            </>
          ),
        },
      }}
    </Dialog>
  );
};

const Content = styled.div`
  strong {
    font-weight: 500;
  }
`;

export { SkyMapConnectDialog };
